import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { T as Tds } from '../design-system/tokens';
import { T } from '../design-system';
import { isMobilePhone, mobilePunchAllowed, PUNCH_DEVICE_MESSAGE } from '../utils/punchDevice';
import { APP_NAME } from '../config/brand';


// Roles that bypass the check-in wall entirely — non-employee accounts have
// no attendance to clock, so the wall would just strand them.
const EXEMPT_ROLES = new Set(['founder', 'security_guard', 'client_portal']);

// Apple's App Review account is not an employee and must never be asked to
// clock in: a reviewer who cannot get past this wall sees an app that does
// nothing, which is a guaranteed rejection. Set REACT_APP_CHECKIN_EXEMPT_EMAILS
// (comma-separated) for such accounts.
const EXEMPT_EMAILS = new Set(String(process.env.REACT_APP_CHECKIN_EXEMPT_EMAILS || '')
  .split(',').map((e) => e.trim().toLowerCase()).filter(Boolean));

export function useCheckInGate() {
  const { user } = useAuth();
  const [checkedIn, setCheckedIn] = useState(null); // null = loading
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    if (!user || EXEMPT_ROLES.has(user.role) || EXEMPT_EMAILS.has((user.email || '').toLowerCase())) {
      setCheckedIn(true);
      return;
    }
    try {
      const { data } = await api.get('/attendance/today');
      // Clocked in = has a clockIn time (clockOut may or may not exist — both count)
      setCheckedIn(!!(data?.clockIn));
    } catch {
      // If the attendance check itself fails, let them through rather than hard-blocking
      setCheckedIn(true);
    }
  }, [user]);

  useEffect(() => { check(); }, [check]);

  const clockIn = async (type = 'office') => {
    setActionLoading(true);
    setError(null);
    try {
      await api.post('/attendance/clock-in', { type });
      setCheckedIn(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Clock-in failed. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  return { checkedIn, actionLoading, error, clockIn };
}

export default function CheckInWall() {
  const { user, logout } = useAuth();
  const { actionLoading, error, clockIn } = useCheckInGate();
  const [type, setType] = useState('office');
  // Same rule as the Attendance page, fetched not assumed. Until it arrives the
  // phone stays blocked, so a slow request cannot hand out permission.
  const [punchRules, setPunchRules] = useState(null);
  useEffect(() => {
    let live = true;
    api.get('/attendance/rules')
      .then(r => { if (live) setPunchRules(r.data?.rules || {}); })
      .catch(() => { if (live) setPunchRules({}); });
    return () => { live = false; };
  }, []);
  const onMobile = isMobilePhone() && !mobilePunchAllowed(punchRules);

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' });

  return (
    <div style={{
      height: '100vh', width: '100vw',
      background: `linear-gradient(135deg, #F8F6FF 0%, ${T.infoBg} 50%, #F4F2F7 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Plus Jakarta Sans, Inter, sans-serif',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 20,
        padding: '48px 44px',
        width: 420,
        maxWidth: '90vw',
        boxShadow: '0 8px 48px rgba(177,162,216,0.18), 0 2px 8px rgba(0,0,0,0.06)',
        textAlign: 'center',
      }}>
        {/* Logo */}
        <div style={{ fontSize: Tds.type.sm, fontWeight: 800, letterSpacing: '0.12em', color: T.info, marginBottom: 28 }}>
          {APP_NAME.toUpperCase()}
        </div>

        {/* Clock icon */}
        <div style={{
          width: 64, height: 64, borderRadius: 18,
          background: 'linear-gradient(135deg, #EDE9FF, #E0D9F5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: Tds.type.xxxl,
        }}>
          ⏱
        </div>

        <div style={{ fontSize: Tds.type.xl, fontWeight: 700, color: T.text, marginBottom: 6 }}>
          Clock in to continue
        </div>
        <div style={{ fontSize: Tds.type.sm, color: T.textSec, marginBottom: 4 }}>
          {dateStr}
        </div>
        <div style={{ fontSize: Tds.type.xxxl, fontWeight: 800, color: T.info, marginBottom: 28 }}>
          {timeStr}
        </div>

        {/* User chip */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#F8F6FF', border: '1px solid #E8E2F5',
          borderRadius: 100, padding: '6px 14px 6px 6px',
          marginBottom: 28,
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: 8,
            background: `linear-gradient(135deg, ${T.info}, #9B8FC4)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: Tds.type.xs, fontWeight: 700, color: '#fff',
          }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <span style={{ fontSize: Tds.type.sm, fontWeight: 600, color: '#3A4258' }}>
            {user?.name || user?.email}
          </span>
        </div>

        {onMobile ? (
          <div style={{
            background: T.warningBg, border: `1px solid ${T.warningBg}`,
            borderRadius: 12, padding: '16px 18px',
            fontSize: Tds.type.sm, color: T.warning, marginBottom: 16, textAlign: 'center',
            lineHeight: 1.5,
          }}>
            <div style={{ fontSize: Tds.type.xxl, marginBottom: 8 }}>🚫</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Mobile not permitted</div>
            Clock-in must be done from an office computer, tablet, or laptop. Please use a desktop or iPad.
          </div>
        ) : (
          <>
            {/* Type selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[
                { value: 'office', label: '🏢 Office' },
                { value: 'wfh',    label: '🏠 WFH' },
                { value: 'away',   label: '✈️ Away' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setType(opt.value)}
                  style={{
                    flex: 1, padding: '9px 0', borderRadius: 10, fontSize: Tds.type.xs, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                    border: type === opt.value ? `2px solid ${T.info}` : '2px solid #E8E2F5',
                    background: type === opt.value ? '#EDE9FF' : '#F9F8FC',
                    color: type === opt.value ? '#7C6BAA' : T.textSec,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
                borderRadius: 8, padding: '10px 14px',
                fontSize: Tds.type.xs, color: T.danger, marginBottom: 16, textAlign: 'left',
              }}>
                {error}
              </div>
            )}

            {/* Clock in button */}
            <button
              onClick={() => clockIn(type)}
              disabled={actionLoading}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 12,
                background: actionLoading ? '#D1C8EC' : `linear-gradient(135deg, ${T.info}, #9B8FC4)`,
                color: '#fff', border: 'none', fontSize: Tds.type.md, fontWeight: 700,
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.15s',
                marginBottom: 12,
              }}
            >
              {actionLoading ? 'Clocking in…' : `Clock In — ${type === 'office' ? 'Office' : type === 'wfh' ? 'WFH' : 'Away'}`}
            </button>
          </>
        )}

        <button
          onClick={logout}
          style={{
            background: 'none', border: 'none', color: T.textMuted,
            fontSize: Tds.type.xs, cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
